<template>
  <div ref="wrapper" class="relative">
    <!-- Fechado: botão normal -->
    <button
      v-if="!open"
      type="button"
      class="form-select w-full pl-4 pr-3 py-2 text-sm flex items-center gap-2 text-left"
      :class="[disabled ? 'opacity-50 cursor-not-allowed' : '', inputClass ?? '']"
      :disabled="disabled"
      @click="openPicker"
    >
      <component v-if="selectedOption?.icon" :is="selectedOption.icon" class="w-4 h-4 shrink-0" :class="selectedOption.iconClass" />
      <span class="flex-1 min-w-0 truncate" :class="!selectedLabel ? 'text-slate-400 dark:text-slate-500' : ''">
        {{ selectedLabel || placeholder }}
      </span>
      <svg class="w-4 h-4 shrink-0 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <!-- Aberto: o próprio campo vira input de busca -->
    <div v-else class="relative flex items-center">
      <component
        v-if="selectedOption?.icon"
        :is="selectedOption.icon"
        class="absolute left-3 w-4 h-4 shrink-0 pointer-events-none"
        :class="selectedOption.iconClass"
      />
      <input
        ref="searchRef"
        v-model="search"
        type="text"
        class="form-input w-full text-sm py-2"
        :class="[selectedOption?.icon ? 'pl-8 pr-4' : 'px-4', inputClass ?? '']"
        :placeholder="selectedLabel || placeholder"
        @keydown.escape="close"
        @keydown.enter.prevent="selectFirst"
      />
    </div>

    <!-- Dropdown: teleportado para body com posição fixed -->
    <Teleport to="body">
      <Transition :name="dropdownUp ? 'dropdown-up' : 'dropdown'">
        <div
          v-if="open"
          ref="dropdownRef"
          :style="dropdownStyle"
          class="fixed z-[9999] rounded-lg border shadow-lg text-sm"
          :class="dropdownClasses"
        >
          <ul class="max-h-52 overflow-y-auto py-1">
            <li v-for="opt in filteredOptions" :key="opt.value ?? '__null__'">
              <button
                type="button"
                class="w-full flex items-center gap-2.5 py-2.5 transition-colors text-left"
                :class="[
                  optionHoverClass,
                  opt.indent ? 'pl-7 pr-4' : 'px-4',
                  opt.isGroup ? 'font-semibold text-slate-800 dark:text-slate-200' : '',
                  isSelected(opt.value)
                    ? `font-medium text-slate-900 dark:text-slate-100 ${optionSelectedClass}`
                    : opt.isGroup ? '' : 'text-slate-700 dark:text-slate-300'
                ]"
                @mousedown.prevent
                @click="select(opt.value)"
              >
                <component v-if="opt.icon" :is="opt.icon" class="w-4 h-4 shrink-0" :class="opt.iconClass" />
                {{ opt.label }}
              </button>
            </li>
            <li v-if="filteredOptions.length === 0" class="px-4 py-3 text-slate-400 dark:text-slate-500 text-center">
              Nenhum resultado
            </li>
          </ul>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue'

const props = defineProps<{
  modelValue: string | number | null
  options: { value: string | number | null; label: string; icon?: object; iconClass?: string; indent?: boolean; isGroup?: boolean }[]
  placeholder?: string
  disabled?: boolean
  inputClass?: string
  // Sem efeito — paleta única zinc no dark mode (ver Modal.vue). Mantida só
  // por compatibilidade com os usos que passam variant="gray".
  variant?: 'default' | 'gray'
}>()
const emit = defineEmits<{ 'update:modelValue': [value: string | number | null] }>()

const dropdownClasses = 'border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-800'
const optionHoverClass = 'hover:bg-slate-50 dark:hover:bg-zinc-700/60'
const optionSelectedClass = 'bg-slate-50/80 dark:bg-zinc-700/40'

const open = ref(false)
const search = ref('')
const wrapper = ref<HTMLElement | null>(null)
const dropdownRef = ref<HTMLElement | null>(null)
const searchRef = ref<HTMLInputElement | null>(null)
const dropdownStyle = ref<Record<string, string>>({})
const dropdownUp = ref(false)

const updateDropdownPosition = () => {
  if (!wrapper.value) return
  const rect = wrapper.value.getBoundingClientRect()
  const spaceBelow = window.innerHeight - rect.bottom
  const spaceAbove = rect.top
  const DROPDOWN_HEIGHT = 224
  const openUp = spaceBelow < DROPDOWN_HEIGHT && spaceAbove > spaceBelow
  dropdownUp.value = openUp
  if (openUp) {
    dropdownStyle.value = {
      bottom: `${window.innerHeight - rect.top + 4}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
    }
  } else {
    dropdownStyle.value = {
      top: `${rect.bottom + 4}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
    }
  }
}

const selectedOption = computed(() => {
  if (props.modelValue === null || props.modelValue === undefined || props.modelValue === '') return null
  return props.options.find(o => o.value === props.modelValue
    || (o.value != null && String(o.value) === String(props.modelValue))) ?? null
})

const selectedLabel = computed(() => selectedOption.value?.label ?? '')

const filteredOptions = computed(() => {
  const q = search.value.trim().toLowerCase()
  return q ? props.options.filter(o => o.label.toLowerCase().includes(q)) : props.options
})

const isSelected = (val: string | number | null) =>
  val === props.modelValue || (val != null && props.modelValue != null && String(val) === String(props.modelValue))

const openPicker = async () => {
  if (props.disabled) return
  updateDropdownPosition()
  open.value = true
  search.value = ''
  await nextTick()
  searchRef.value?.focus()
}

const close = () => {
  open.value = false
  search.value = ''
}

const select = (value: string | number | null) => {
  emit('update:modelValue', value)
  open.value = false
  search.value = ''
}

const selectFirst = () => {
  if (filteredOptions.value.length > 0) {
    select(filteredOptions.value[0].value)
  }
}

const onClickOutside = (e: MouseEvent) => {
  const target = e.target as Node
  if (
    wrapper.value && !wrapper.value.contains(target) &&
    dropdownRef.value && !dropdownRef.value.contains(target)
  ) close()
}

const onScrollOrResize = () => {
  if (open.value) updateDropdownPosition()
}

onMounted(() => {
  document.addEventListener('mousedown', onClickOutside)
  window.addEventListener('scroll', onScrollOrResize, { capture: true, passive: true })
  window.addEventListener('resize', onScrollOrResize, { passive: true })
})
onUnmounted(() => {
  document.removeEventListener('mousedown', onClickOutside)
  window.removeEventListener('scroll', onScrollOrResize, { capture: true })
  window.removeEventListener('resize', onScrollOrResize)
})
</script>

<style scoped>
.dropdown-enter-active, .dropdown-leave-active { transition: opacity 0.12s ease, transform 0.12s ease; }
.dropdown-enter-from, .dropdown-leave-to { opacity: 0; transform: translateY(-4px); }
.dropdown-up-enter-active, .dropdown-up-leave-active { transition: opacity 0.12s ease, transform 0.12s ease; }
.dropdown-up-enter-from, .dropdown-up-leave-to { opacity: 0; transform: translateY(4px); }
</style>
